import React, { useRef, useState } from 'react';
import { FLOW } from '../content/narrative';

/* The design loop as a tab strip. The pills looked selectable before they were
   — a visitor reading the fixed correlation panel underneath reasonably expects
   to be able to pick the other steps. Now they can. Correlate opens by default
   because it's the compounding mechanism, not merely step seven, and keeping
   the step copy behind selection means the loop never becomes a wall of seven
   descriptions. */

const sans = "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const mono = "ui-monospace, 'SF Mono', 'Cascadia Code', Menlo, Consolas, monospace";

const LoopSelector = () => {
  const [active, setActive] = useState(FLOW.defaultStep);
  const tabRefs = useRef([]);

  /* Roving focus: ←/→ move between steps, Home/End jump to the ends. */
  const onKeyDown = (e) => {
    const last = FLOW.steps.length - 1;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = active === last ? 0 : active + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = active === 0 ? last : active - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  const step = FLOW.steps[active];

  return (
    <div>
      <div
        role="tablist"
        aria-label="The engineering loop"
        onKeyDown={onKeyDown}
        className="flex flex-wrap items-center gap-x-2 gap-y-2"
      >
        {FLOW.steps.map((s, i) => {
          const on = i === active;
          return (
            <React.Fragment key={s.id}>
              {i > 0 && (
                <span aria-hidden style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)' }}>→</span>
              )}
              <button
                type="button"
                role="tab"
                id={`loop-tab-${s.id}`}
                aria-selected={on}
                aria-controls="loop-panel"
                tabIndex={on ? 0 : -1}
                ref={(el) => { tabRefs.current[i] = el; }}
                onClick={() => setActive(i)}
                style={{
                  fontFamily: mono,
                  fontSize: 'var(--t-sm)',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: on ? 'var(--wb-on-ink)' : 'var(--wb-ink)',
                  background: on ? 'var(--wb-orange)' : 'var(--wb-paper)',
                  border: `1px solid ${on ? 'var(--wb-orange)' : 'var(--wb-line)'}`,
                  borderRadius: 999,
                  padding: '7px 14px',
                  cursor: 'pointer',
                }}
              >
                {s.name}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div
        role="tabpanel"
        id="loop-panel"
        aria-labelledby={`loop-tab-${step.id}`}
        tabIndex={0}
        style={{ marginTop: 12, padding: '24px 22px', background: 'var(--wb-paper)', border: '1px solid var(--wb-orange)', borderRadius: 14 }}
      >
        <div style={{ fontFamily: mono, fontSize: 'var(--t-sm)', color: 'var(--wb-orange)', fontWeight: 800, letterSpacing: '0.1em' }}>
          {step.id} · {step.name.toUpperCase()}
        </div>
        <p className="wb-display" style={{ fontSize: 'var(--t-lg)', lineHeight: 1.1, margin: '12px 0 0' }}>{step.q}</p>
        <p style={{ fontFamily: sans, fontSize: 'var(--t-md)', color: 'var(--wb-muted)', margin: '12px 0 0', maxWidth: 760, lineHeight: 1.6 }}>
          {step.body}
        </p>
      </div>
    </div>
  );
};

export default LoopSelector;
